import {CircleHelp} from "lucide-react";
import type React from "react";
import {Suspense} from "react";
import {useGetContentItemById20} from "#/_generated/umbraco/delivery-api/features/content/content.ts";
import {
    type FaqListBlockElementModel,
    type FaqPageContentModel,
    FaqPageContentModelContentType,
    type FaqPageContentResponseModel,
} from "#/_generated/umbraco/delivery-api/models";
import {Heading} from "#/_packages/umbrella/components/ui/primitives/heading";
import {Accordion} from "#/_packages/umbrella/features/accordion/components/accordion";
import type {HeadingLevel} from "#/_packages/umbrella/types";
import {tryGetHeadingLevel} from "#/_packages/umbrella/utils";

type FaqListProps = {
    content: FaqListBlockElementModel;
};

export default function FaqList({content}: FaqListProps) {
    const properties = content.properties;
    const variation = getVariation(properties?.variation);

    const sections = (properties?.sections ?? []).filter(
        (section): section is FaqPageContentModel =>
            section.contentType === FaqPageContentModelContentType.faqPage,
    );
    const headingLevel = tryGetHeadingLevel(properties?.headingLevel, 3);
    return (
        <section className={`section-list section-list-${variation}`}>
            <div className="read-width">
                <FaqListVariation
                    variation={variation}
                    sections={sections}
                    headingLevel={headingLevel}
                />
            </div>
        </section>
    );
}

type Variation = ReturnType<typeof getVariation>;

type VariationProps = {
    sections: FaqPageContentModel[];
    headingLevel: HeadingLevel;
};

function FaqListVariation({
                              variation,
                              ...props
                          }: VariationProps & { variation: Variation }) {
    switch (variation) {
        case "faq":
            return <FaqVariation {...props} />;
        case "accordion":
            return <AccordionVariation {...props} />;
        case "stepper":
            return <StepperVariation {...props} />;
        case "show-all":
            return <ShowAllVariation {...props} />;
    }
}

function FaqVariation({sections, headingLevel}: VariationProps) {
    return (
        <Accordion.Group className="faq">
            {sections.map((section) => (
                <FaqAccordionItem
                    key={section.id}
                    section={section}
                    headingLevel={headingLevel}
                    leadingIcon={
                        <CircleHelp className="icon mr-2 shrink-0" size={24} aria-hidden/>
                    }
                    showDisclosureIcon={false}
                />
            ))}
        </Accordion.Group>
    );
}

function AccordionVariation({sections, headingLevel}: VariationProps) {
    return (
        <Accordion.Group>
            {sections.map((section) => (
                <FaqAccordionItem
                    key={section.id}
                    section={section}
                    headingLevel={headingLevel}
                />
            ))}
        </Accordion.Group>
    );
}

function StepperVariation({sections, headingLevel}: VariationProps) {
    return (
        <ol className="list-decimal">
            {sections.map((section) => (
                <li key={section.id} className="mb-0-last">
                    <Heading level={headingLevel} display={4}>
                        {getHeading(section)}
                    </Heading>
                    <FaqAccordionItemBody contentId={section.id}/>
                </li>
            ))}
        </ol>
    );
}

function ShowAllVariation({sections, headingLevel}: VariationProps) {
    return sections.map((section) => (
        <ShowAllItem
            key={section.id}
            section={section}
            headingLevel={headingLevel}
        />
    ));
}

type FaqSectionProps = {
    section: FaqPageContentModel;
    headingLevel: HeadingLevel;
};

function ShowAllItem({section, headingLevel}: FaqSectionProps) {
    return (
        <>
            <Heading level={headingLevel}>{getHeading(section)}</Heading>
            <FaqAccordionItemBody contentId={section.id}/>
        </>
    );
}

function FaqAccordionItem({
                              section,
                              headingLevel,
                              leadingIcon,
                              showDisclosureIcon = true,
                          }: FaqSectionProps & {
    leadingIcon?: React.ReactNode;
    showDisclosureIcon?: boolean;
}) {
    const heading = getHeading(section);
    const panelId = `accordion-${section.id}-collapse-${toHtmlId(heading)}`;

    return (
        <Accordion.Item id={panelId}>
            {({isExpanded}) => (
                <>
                    <Accordion.Heading
                        className={"flex justify-between"}
                        level={headingLevel}
                        isExpanded={isExpanded}
                    >
                        <div className="flex flex-wrap">
                            {leadingIcon}
                            <span className="flex-1">
								<Accordion.HeadingContent heading={heading}/>
							</span>
                        </div>
                        {showDisclosureIcon ? (
                            <Accordion.Icon isExpanded={isExpanded}/>
                        ) : null}
                    </Accordion.Heading>
                    <Accordion.Panel isExpanded={isExpanded}>
                        <FaqAccordionItemBody contentId={section.id}/>
                    </Accordion.Panel>
                </>
            )}
        </Accordion.Item>
    );
}

const FaqAccordionItemBodyRichText = ({contentId}: { contentId: string }) => {
    const {data} = useGetContentItemById20(contentId);
    const content = data?.data as FaqPageContentResponseModel;

    return <RichText body={content?.properties?.body?.markup}/>;
};

const FaqAccordionItemBody = ({contentId}: { contentId: string }) => {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <FaqAccordionItemBodyRichText contentId={contentId}/>
        </Suspense>
    );
};

function getHeading(section: FaqPageContentModel) {
    return section.properties?.heading ?? section.name ?? "";
}

function RichText({body}: { body?: string }) {
    if (!body) return null;

    // CMS rich text is trusted markup produced by the Umbraco Delivery API.
    return (
        <div
            className="prose"
            style={{maxWidth: "42rem"}}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Rich text markup comes from Umbraco CMS.
            dangerouslySetInnerHTML={{__html: body}}
        />
    );
}

function getVariation(value?: string | null) {
    switch (value) {
        case "Steg-för-steg":
            return "stepper";
        case "FAQ":
            return "faq";
        case "Dragspel":
            return "accordion";
        default:
            return "show-all";
    }
}

function toHtmlId(value: string) {
    return (
        value
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "") || "section"
    );
}
